import { useMutation, useQueryClient } from "@tanstack/react-query";
import { editUser } from "@core/services/edit-user";
import type {
  ApiErrorResponse,
  ServiceError,
  TEditUserSchema,
  TUser,
} from "@core/types";
import { useAuthStore } from "@modules/auth/store/useAuthStore";
import { usePromiseToast } from "./usePromiseToast";
import { useTranslate } from "@core/hooks";

export function useEditUser() {
  const queryClient = useQueryClient();
  const { toastify } = usePromiseToast();
  const { t } = useTranslate("auth");

  return useMutation<void, ApiErrorResponse, TEditUserSchema>({
    mutationFn: (payload: TEditUserSchema) => {
      const controller = new AbortController();

      const promise = editUser(payload, controller.signal);

      void toastify(
        t,
        promise,
        {
          loading: "profile.edit.toast.loading",
          success: "profile.edit.toast.success",
          error: {
            key: (err: unknown) =>
              "errors." + ((err as ServiceError).code ?? "UNKNOWN_ERROR"),
          },
        },
        { autoCloseMs: 4000 },
      );

      return promise;
    },
    onSuccess: (_data, payload) => {
      const old =
        queryClient.getQueryData<TUser>(["user"]) ??
        useAuthStore.getState().user;
      if (!old) return;

      const updatedUser: TUser = { ...old, ...payload };

      queryClient.setQueryData(["user"], updatedUser);
      useAuthStore.getState().setUser(updatedUser);
    },
  });
}
